import styled from "@emotion/styled";
import {groupBy} from "lodash";
import {useAuthState} from "../../../providers/AuthProvider";
import {navigateToCmsOrder} from "../../../services/CmsService";
import {useOktaAuth} from "@okta/okta-react";
import {usePreferenceContext} from "../../../providers/PreferenceProvider";
import {StyledLink} from "../../common/StyledLink";
export type AddressValidationItem = {
  identifier: string;
  address: string;
  orderId?: number;
  groupId?: string;
  jobId?: number;
};

interface AddressValidationContentProps {
  type: "stop-address" | "driver-address";
  items: Array<AddressValidationItem>;
  isGrouped?: boolean;
  showGroupLinks?: boolean;
}

const AddressValidationContent = ({type, items, isGrouped, showGroupLinks = false}: AddressValidationContentProps) => {
  const {tenant} = useAuthState();
  const {oktaAuth} = useOktaAuth();
  const {tenantPreferences} = usePreferenceContext();
  const firstLine =
    type === "stop-address"
      ? "The below job(s) contain stops with invalid addresses:"
      : "The below driver(s) have invalid start/end locations:";
  const endLine =
    type === "stop-address"
      ? "These jobs will be unselected automatically, please click the link to the job and adjust the address(es) if you'd like to reselect."
      : "Please make the necessary adjustments and click Next again.";

  const handleGoToCmsOrder = async (orderID: number | undefined) => {
    await navigateToCmsOrder(tenant!, oktaAuth.getAccessToken() ?? "", orderID, tenantPreferences);
  };

  const renderGroupTitleWithLink = (groupId: string, orderId: number | undefined) => {
    const [groupTitle, groupLink] = groupId.split(" ");
    return (
      <GroupTitle data-testid="invalid-content">
        - {groupTitle}{" "}
        <StyledLink data-testid={`invalid-group-link-${groupLink}`} onClick={() => void handleGoToCmsOrder(orderId)}>
          {groupLink}
        </StyledLink>
        :
      </GroupTitle>
    );
  };

  const renderContent = () => {
    if (isGrouped) {
      const groupedItems = groupBy(items, (item) => item.groupId || "Ungrouped");
      return Object.entries(groupedItems).map(([groupId, groupItems]) => {
        const orderID = groupItems[0].orderId;
        return (
          <Group key={groupId} style={{paddingLeft: "16px"}} data-testid={`group-${groupId}`}>
            {showGroupLinks ? (
              renderGroupTitleWithLink(groupId, orderID)
            ) : (
              <GroupTitle data-testid="invalid-content">- {groupId}:</GroupTitle>
            )}
            <GroupItemList>
              {groupItems.map((item) => (
                <Item data-testid={`item-${item.identifier}`} key={item.identifier}>
                  <ItemIdentifier>{item.identifier}: </ItemIdentifier>
                  {item.address}
                </Item>
              ))}
            </GroupItemList>
          </Group>
        );
      });
    } else {
      return (
        <GroupItemList style={{paddingLeft: "16px"}}>
          {items.map((item) => (
            <Item
              data-testid={`item-${item.identifier}`}
              key={item.identifier}
              style={{listStyleType: "none", marginBottom: "8px"}}
            >
              <ItemIdentifier>- {item.identifier}: </ItemIdentifier>
              {item.address}
            </Item>
          ))}
        </GroupItemList>
      );
    }
  };

  return (
    <Container data-testid="address-validation-content">
      <Text style={{marginBottom: "12px"}}>{firstLine}</Text>
      <ScrollList>{renderContent()}</ScrollList>
      <Text style={{marginTop: "12px"}}>{endLine}</Text>
    </Container>
  );
};

export default AddressValidationContent;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Text = styled.p``;

const ScrollList = styled.div`
  overflow-y: auto;
  max-height: 70vh;
`;

const Group = styled.div``;

const GroupTitle = styled.h4`
  margin: 4px 0;
`;

const GroupItemList = styled.ul`
  margin-top: 0;
  margin-bottom: 16px;
`;

const Item = styled.li`
  margin-bottom: 4px;
  padding: 0;
`;

const ItemIdentifier = styled.span`
  font-weight: bold;
`;
